import { providerAvailabilityById } from "./providerAvailability.js";

import { ACTIVE_BOOKING_URL } from "../config/bookingLinks.js";
// Color system:
// Senior Psychotherapist / Founder → Purple (#7D54FC)
// Associate Psychotherapist (MHC-LP) → Blue (#3C6FF8)
// Psychotherapist (MHC-I) → Green (#4A7C59)
// Support Staff → Neutral (#8B7355)

export interface TeamMember {
  id: string;
  name: string;
  credentials: string;
  role: string;
  type: "clinical" | "support";
  price: string;
  individualPrice: string;
  couplesPrice?: string;
  priceNote: string;
  focus: string;
  shortBio: string;
  fullBio: string;
  modalities: string[];
  img?: string;
  availability: string;
  accentColor: string;
  accentBg: string;
  tagColor: string;
  tagBg: string;
  specialties: string[];
  education: string[];
  approach: string;
  languages?: string[];
  scheduleNote?: string;
  ctaText?: string;
  ctaLink?: string;
  worksWith?: string[];
  acceptingNewClients?: boolean;
  acceptingIndividualClients?: boolean;
  acceptingCouplesClients?: boolean;
  acceptingChildClients?: boolean;
  acceptingTeenClients?: boolean;
  acceptingAdultClients?: boolean;
  acceptingGroupClients?: boolean;
  serviceFormats?: ("in-person" | "telehealth")[];
  currentAvailabilityFormats?: ("in-person" | "telehealth")[];
  isSpanishSpeaking?: boolean;
  availabilityNote?: string;
  bookingVisibility?: string;
  displayOrder?: number;
  isAssociate?: boolean;
}

// ─── CLINICAL TEAM ────────────────────────────────────────────────────────────

// Senior / Founder - purple
const SENIOR = "#7D54FC";
const SENIOR_BG = "rgba(125,84,252,0.08)";

// Associate (MHC-LP) - blue
const ASSOC = "#3C6FF8";
const ASSOC_BG = "rgba(60,111,248,0.07)";

// Psychotherapist (MHC-I) - green
const GREEN = "#4A7C59";
const GREEN_BG = "rgba(74,124,89,0.07)";

// Support - warm neutral
const SUPPORT = "#8B7355";
const SUPPORT_BG = "rgba(139,115,85,0.07)";

const WORKS_WITH_ORDER = [
  "Children",
  "Teens",
  "Adults",
  "Parents",
  "Couples",
  "Groups",
];

export function formatWorksWith(worksWith: string[] = []): string {
  const orderedWorksWith = WORKS_WITH_ORDER.filter((label) =>
    worksWith.includes(label)
  );
  const remainingWorksWith = worksWith.filter(
    (label) => !WORKS_WITH_ORDER.includes(label)
  );
  const labels = [...orderedWorksWith, ...remainingWorksWith];

  if (labels.length <= 2) {
    return labels.join(" & ");
  }

  return `${labels.slice(0, -1).join(", ")}, & ${labels.at(-1)}`;
}

const providerAvailabilityFor = (id: keyof typeof providerAvailabilityById) => ({
  acceptingNewClients: providerAvailabilityById[id].acceptingNewClients,
  acceptingIndividualClients: providerAvailabilityById[id].acceptingIndividualClients,
  acceptingCouplesClients: providerAvailabilityById[id].acceptingCouplesClients,
  acceptingChildClients: providerAvailabilityById[id].acceptingChildClients,
  acceptingTeenClients: providerAvailabilityById[id].acceptingTeenClients,
  acceptingAdultClients: providerAvailabilityById[id].acceptingAdultClients,
  acceptingGroupClients: providerAvailabilityById[id].acceptingGroupClients,
  serviceFormats: providerAvailabilityById[id].serviceFormats,
  currentAvailabilityFormats: providerAvailabilityById[id].currentAvailabilityFormats,
  isSpanishSpeaking: providerAvailabilityById[id].isSpanishSpeaking,
  availabilityNote: providerAvailabilityById[id].availabilityNote,
  bookingVisibility: providerAvailabilityById[id].bookingVisibility,
  displayOrder: providerAvailabilityById[id].displayOrder,
});

export const teamMembers: TeamMember[] = [
  // ── Sathuu Sivakumaran - Associate Psychotherapist ─────────────────────────
  {
    id: "sathuu-sivakumaran",
    name: "Sathuu Sivakumaran",
    credentials: "MA, MHC-LP",
    role: "Associate Psychotherapist",
    type: "clinical",
    price: "$125 / session",
    individualPrice: "$125",
    priceNote: "",
    focus: "Stress · Relationships · Self-Discovery",
    shortBio:
      "Sathuu supports clients navigating stress, relationship patterns, and questions of identity. Her style is collaborative and grounded. She meets you where you are and helps you move forward.",
    fullBio:
      "Many of the clients Sathuu works with are trying to make sense of competing expectations, cultural influences, and personal goals. Navigating family values, identity, and life decisions across different cultural contexts can create stress, self-doubt, and internal conflict. In therapy, she helps clients slow down, examine these experiences, and develop a clearer understanding of how their beliefs and emotional patterns shape the way they move through the world.\n\nShe works with adolescents and adults navigating anxiety, trauma, depression, and the stress that can come with difficult life experiences. Many of the people she works with feel caught in cycles of overthinking, self-criticism, or emotional overwhelm and want a clearer way to understand and respond to the patterns affecting their lives. She also supports clients navigating relationship challenges, major life transitions, identity questions, burnout, and difficulties with emotional regulation.\n\nHer perspective as a South Asian therapist often allows her to connect with clients who are exploring questions around culture, identity, and expectations from family or community. She aims to create a space where these experiences can be discussed openly and thoughtfully while helping clients develop greater clarity and confidence in their decisions.\n\nShe earned her Master's degree in Mental Health and Wellness Counseling from New York University, where she developed a strong foundation in evidence-based and insight-oriented therapeutic approaches. She is trained in Rational Emotive Behavior Therapy (REBT), an evidence-based approach that helps clients identify and challenge unhelpful beliefs that often contribute to anxiety, emotional distress, and self-defeating patterns.",
    modalities: ["CBT", "REBT", "ACT", "Psychodynamic"],
    availability: "Accepting clients",
    accentColor: ASSOC,
    accentBg: ASSOC_BG,
    tagColor: ASSOC,
    tagBg: ASSOC_BG,
    specialties: [
      "Anxiety",
      "Cultural Identity",
      "Stress and Burnout",
      "Relationship Patterns",
      "Life Transitions",
      "Perfectionism",
    ],
    education: ["MA in Mental Health and Wellness Counseling, New York University"],
    approach:
      "Sathuu integrates cognitive-behavioral and acceptance-based techniques with a relational, psychodynamic lens. She focuses on helping clients recognize unhelpful patterns, build emotional flexibility, and move toward values-based living.",
    languages: ["English", "Tamil"],
    scheduleNote: "Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Teens", "Adults"],
    isAssociate: true,
  },

  // ── Vanessa Braithwaite - Associate Psychotherapist ────────────────────────
  {
    id: "vanessa-braithwaite",
    name: "Vanessa Braithwaite",
    credentials: "MA, MHC-LP",
    role: "Associate Psychotherapist",
    type: "clinical",
    price: "$125 / session",
    individualPrice: "$125",
    priceNote: "",
    focus: "Anxiety · Trauma · Life Transitions",
    shortBio:
      "Vanessa works with individuals navigating anxiety, trauma, depression, and the emotional strain that can come with difficult life experiences. She brings warmth and honesty to every session, helping clients feel genuinely heard from the start.",
    fullBio:
      "As a West-Indian American therapist, Vanessa utilizes her cultural awareness to create a space where clients from diverse backgrounds feel understood and respected. Clients often appreciate her calm and steady presence, as well as the balance of insight and practical strategies they develop together to support meaningful and lasting change.\n\nShe works with adolescents and adults who are navigating anxiety, trauma, depression, grief, and the emotional strain that can come with difficult life experiences. Many of the people she works with feel overwhelmed by persistent worry, emotional exhaustion, or patterns that make it difficult to move forward despite their best efforts. She also supports clients navigating relationship difficulties, major life transitions, identity concerns, burnout, and challenges with emotional regulation. Therapy offers a space to slow down, gain perspective, and develop practical tools for responding to life's challenges with greater clarity and resilience.\n\nShe earned her Master's degree in Mental Health and Wellness Counseling from New York University, where she developed a strong foundation in evidence-based and insight-oriented therapeutic approaches. In therapy, she focuses on helping clients better understand the thoughts, beliefs, and emotional patterns that shape how they experience themselves and their relationships. Her approach is collaborative and tailored to each individual, drawing from cognitive, insight-oriented, and experiential therapies. She is also trained in Rational Emotive Behavior Therapy (REBT), an evidence-based approach that helps people examine and challenge unhelpful beliefs that often contribute to anxiety, self-criticism, and emotional distress.",
    modalities: ["CBT", "REBT", "Psychodynamic", "Mindfulness", "ACT"],
    availability: "Accepting clients",
    accentColor: ASSOC,
    accentBg: ASSOC_BG,
    tagColor: ASSOC,
    tagBg: ASSOC_BG,
    specialties: [
      "Anxiety",
      "Trauma",
      "Depression",
      "Life Transitions",
      "Identity",
      "Grief",
    ],
    education: ["MA in Mental Health and Wellness Counseling, New York University"],
    approach:
      "Vanessa's approach is collaborative and tailored to each individual, drawing from cognitive, insight-oriented, and experiential therapies. She focuses on helping clients better understand the thoughts, beliefs, and emotional patterns that shape how they experience themselves and their relationships.",
    languages: ["English"],
    scheduleNote: "In-person: Larchmont, NY · Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Teens", "Adults"],
    isAssociate: true,
  },

  // ── Neyla Sassi - Senior Psychotherapist ───────────────────────────────────
  {
    id: "neyla-sassi",
    name: "Neyla Sassi",
    credentials: "MS ED, CAS, MHC-LP",
    role: "Senior Psychotherapist",
    type: "clinical",
    price: "$200 / session",
    individualPrice: "$200",
    priceNote: "",
    focus: "Anxiety · Life Stress · Emotional Overwhelm",
    shortBio:
      "Neyla believes meaningful change begins with feeling genuinely understood. She works with children, adolescents, adults, and groups, helping clients explore their experiences and develop healthier ways to respond to life's challenges.",
    fullBio:
      "Neyla believes meaningful change begins with feeling genuinely understood. Her goal is to create a welcoming and supportive space where you feel comfortable exploring your experiences, emotions, and personal challenges at your own pace. Her therapeutic style is warm, collaborative, and empowering. She believes each person has the capacity for growth and resilience, and her role is to help you recognize your strengths while developing insight and practical tools to move forward with greater clarity and confidence.\n\nShe works with children, adolescents, adults, and groups navigating anxiety, life stress, emotional overwhelm, and difficult experiences that can affect relationships, confidence, and overall well-being. Many of the people she works with are looking for support in processing what they've been through and finding healthier ways to respond to the challenges in their lives.\n\nHer experience in the child welfare system and as a school counselor, along with her Master's degree in School Counseling from Lehman College and Advanced Certificate in Mental Health Counseling from Alfred University, has given her valuable insight into how life experiences shape emotional well-being across different stages of life. These experiences continue to inform her work with clients in a way that is compassionate, thoughtful, and grounded.\n\nOutside of her work as a therapist, she enjoys spending time with loved ones, knitting, and taking Zumba classes.",
    modalities: [
      "CBT",
      "Person-Centered",
      "Psychodynamic",
      "Play Therapy",
      "Sand Tray Therapy",
    ],
    availability: "Accepting clients",
    accentColor: SENIOR,
    accentBg: SENIOR_BG,
    tagColor: SENIOR,
    tagBg: SENIOR_BG,
    specialties: [
      "Anxiety",
      "Life Stress",
      "Emotional Overwhelm",
      "Relationship Challenges",
      "Identity",
    ],
    education: [
      "Advanced Certificate in Mental Health Counseling, Alfred University",
      "MS in School Counseling, Lehman College",
    ],
    approach:
      "Neyla's therapeutic style is warm, collaborative, and empowering. She focuses on helping clients recognize their strengths while developing insight and practical tools to move forward with greater clarity and confidence.",
    languages: ["English"],
    scheduleNote: "In-person: Larchmont, NY · Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Children", "Teens", "Adults", "Groups"],
    ...providerAvailabilityFor("neyla-sassi"),
    isAssociate: false,
  },

  // ── Kinder Bradley - Associate Psychotherapist ─────────────────────────────
  {
    id: "kinder-bradley",
    name: "Kinder Bradley",
    credentials: "MHC",
    role: "Associate Psychotherapist",
    type: "clinical",
    price: "$125 / session",
    individualPrice: "$125",
    priceNote: "",
    focus: "Trauma · Life Transitions · Personal Growth",
    shortBio:
      "Kinder works with individuals navigating life transitions, trauma-related experiences, and periods of significant personal change. Her approach is thoughtful, grounded, and collaborative.",
    fullBio:
      "Kinder works with individuals who are navigating life transitions, trauma-related experiences, and periods of significant personal change. She is particularly interested in helping clients understand how past experiences influence present patterns while building resilience and developing healthier ways of moving forward.\n\nHer approach to therapy is thoughtful, grounded, and collaborative. She believes healing often begins when people feel safe enough to explore difficult experiences while also developing practical tools that support emotional regulation, self-understanding, and personal growth. She draws from trauma-informed and evidence-based approaches while maintaining a deep respect for each client's story and pace.\n\nHer professional background in education and advocacy has given her a strong appreciation for the ways systems, relationships, and life experiences shape emotional well-being. She earned her Master's degree in Mental Health Counseling from New York University, where she further developed her clinical skills and trauma-informed perspective.\n\nOutside of her clinical work, she enjoys facilitating a mentorship program for youth and families. These experiences continue to inform the grounded and compassionate perspective she brings to her work with clients.",
    modalities: ["Trauma-Informed", "Strengths-Based", "CBT", "ACT", "REBT"],
    availability: "Accepting clients",
    accentColor: ASSOC,
    accentBg: ASSOC_BG,
    tagColor: ASSOC,
    tagBg: ASSOC_BG,
    specialties: [
      "Trauma",
      "Life Transitions",
      "Personal Growth",
      "Emotional Regulation",
      "Resilience",
    ],
    education: ["MA in Mental Health Counseling, New York University (in progress)"],
    approach:
      "Kinder draws from trauma-informed and evidence-based approaches while maintaining a deep respect for each client's story and pace. She focuses on helping clients build resilience and develop healthier ways of moving forward.",
    languages: ["English"],
    scheduleNote: "In-person: Larchmont, NY · Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Teens", "Adults"],
    isAssociate: true,
  },

  // ── George Lopez - Psychotherapist (MHC-I) ────────────────────────────────
  {
    id: "george-lopez",
    name: "George Lopez",
    credentials: "MHC-I",
    role: "Psychotherapist",
    type: "clinical",
    price: "$65 / session",
    individualPrice: "$65",
    priceNote: "",
    focus: "Anxiety · ADHD · Stress & Burnout",
    shortBio:
      "George brings warmth, curiosity, and genuine energy to his work with children, teens, adults, and groups. He helps clients navigate anxiety, ADHD, stress and burnout, relationships, life transitions, identity, and career concerns.",
    fullBio:
      "George believes therapy can be meaningful without feeling overly formal. He brings warmth, curiosity, and genuine energy into his work, creating a space where clients can be themselves while still having conversations that move somewhere. He values getting to know the person beyond the problem that brought them to therapy and works collaboratively to make the process relevant to what is actually happening in their life.\n\nGeorge works with young children, teens, and adults navigating anxiety, ADHD, stress and burnout, relationship concerns, life transitions, identity shifts, and the pressures that can come with different stages of life. With younger clients, he focuses on helping them better understand their emotions, build confidence, and develop skills they can carry into everyday life. He also enjoys working with professionals and parents balancing personal, family, and professional responsibilities. His background in education and workforce development gives him a practical understanding of challenges ranging from school and growing independence to career changes, job loss, shifting responsibilities, and figuring out what comes next.\n\nGeorge helps clients better understand what may be getting in the way and develop strategies that make sense within the context of their actual lives. His work is strengths-based, culturally informed, and grounded in the belief that people are more than the challenges they are facing. He is currently completing his Master’s degree in Counseling for Mental Health and Wellness at New York University, where he continues to deepen his clinical training and refine an approach that helps clients create change that feels both realistic and meaningful.\n\nOutside of therapy, George brings the same curiosity and energy to the rest of his life. He volunteers, plays recreational soccer, experiments with new recipes, plays video games, and enjoys exploring different parts of New York City with his family.",
    modalities: ["ACT", "Psychoeducation", "Person-Centered", "Strengths-Based"],
    availability: "Accepting clients",
    accentColor: GREEN,
    accentBg: GREEN_BG,
    tagColor: GREEN,
    tagBg: GREEN_BG,
    specialties: [
      "Anxiety",
      "ADHD",
      "Stress",
      "Burnout",
      "Relationship Concerns",
      "Life Transitions",
      "Identity-Related Concerns",
      "Career / Vocational Concerns",
    ],
    education: [
      "MA in Counseling for Mental Health and Wellness, New York University (in progress)",
    ],
    approach:
      "George’s approach is culturally informed, strengths-based, and person-centered, with an emphasis on making therapy useful in everyday life. He draws from Acceptance and Commitment Therapy (ACT), psychoeducation, and other approaches based on each client’s age, needs, and goals. He helps clients better understand the connection between their thoughts, emotions, values, and choices while developing practical tools they can use beyond the therapy room. His style is engaged and conversational, using curiosity and honest reflection to create space for meaningful exploration and change.",
    languages: ["English", "Spanish"],
    scheduleNote: "In-person: Larchmont, NY · Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Children", "Teens", "Adults", "Groups"],
    ...providerAvailabilityFor("george-lopez"),
    isAssociate: false,
  },

  // ── Maddie Lula - Psychotherapist (MHC-I) ─────────────────────────────────
  {
    id: "maddie-lula",
    name: "Maddie Lula",
    credentials: "MHC-I",
    role: "Psychotherapist",
    type: "clinical",
    price: "$65 / session",
    individualPrice: "$65",
    priceNote: "",
    focus: "Identity · Relationships · Life Issues",
    shortBio:
      "Maddie believes therapy should feel like a space where you can slow down and be honest with yourself. She works with adolescents and adults navigating stress, anxiety, identity exploration, and the pressure to meet expectations.",
    fullBio:
      "Maddie believes therapy should feel like a place where you can be honest about who you are, including the parts of yourself you may still be figuring out. She brings curiosity, openness, and genuineness into the room and wants clients to feel comfortable exploring their thoughts, emotions, identities, and experiences without pressure to have everything figured out.\n\nShe works with young children, adolescents, and adults navigating anxiety, stress, emotional regulation challenges, life transitions, identity development, relationship concerns, and the pressures that can come with high expectations. Maddie enjoys helping children better understand and express their emotions, supporting teens as they navigate independence and identity, and working with adults who want a stronger sense of self and more fulfilling relationships. She also works with parents navigating the challenges of raising children and strengthening their relationships at home.\n\nBefore entering the mental health field, Maddie was a student-athlete, an experience that gave her firsthand insight into discipline, resilience, performance pressure, and the expectations we can place on ourselves. She is currently completing her Master’s degree in Mental Health Counseling at Mercy University. Outside of her work, she enjoys spending time at the beach, learning new instruments, and practicing yoga.",
    modalities: [
      "Internal Family Systems (IFS)",
      "Person-Centered Therapy",
      "Mindfulness",
      "Experiential Therapy",
      "Humanistic Therapy",
    ],
    availability: "Accepting clients",
    accentColor: GREEN,
    accentBg: GREEN_BG,
    tagColor: GREEN,
    tagBg: GREEN_BG,
    specialties: [
      "Anxiety",
      "Stress",
      "Emotional Regulation",
      "Life Transitions",
      "Identity Development",
      "Relationship Concerns",
      "Performance Pressure / High Expectations",
      "Parenting / Parent Support",
    ],
    education: ["MA in Mental Health Counseling, Mercy University (in progress)"],
    approach:
      "Maddie’s approach is culturally informed, person-centered, and affirming. She works well with LGBTQ+ clients and understands that questions about identity, belonging, relationships, family expectations, and self-expression can show up differently at different stages of life. She takes time to understand each client in the context of their experiences and the different influences that have shaped how they see themselves and relate to others. Drawing from Internal Family Systems (IFS), mindfulness, and other experiential approaches, she may use parts work to help clients become curious about different sides of themselves, including those that may feel contradictory or difficult to understand. Her approach remains flexible and responsive to what each client needs.",
    languages: ["English"],
    scheduleNote: "In-person: Larchmont, NY · Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Children", "Teens", "Adults", "Groups"],
    ...providerAvailabilityFor("maddie-lula"),
    isAssociate: false,
  },

  // ── Jacqueline Suarez - Senior Psychotherapist ───────────────────────────
  {
    id: "jacqueline-suarez",
    name: "Jacqueline Suarez",
    credentials: "MSW, LCSW",
    role: "Senior Psychotherapist",
    type: "clinical",
    price: "$200 / session",
    individualPrice: "$200",
    couplesPrice: "$200",
    priceNote: "",
    focus: "Anxiety · Identity · Couples",
    shortBio:
      "Jacqueline is a warm, caring, and dedicated therapist who creates a supportive environment where clients feel comfortable speaking openly. She works with adolescents, adults, and couples, and is bilingual in English and Spanish.",
    fullBio:
      "Jacqueline Suarez describes herself as a warm, caring, and dedicated person, and those qualities shape the way she approaches her work as a therapist. Her goal is to create a supportive environment where clients feel comfortable speaking openly, being themselves, and exploring what they are going through without fear of judgment.\n\nShe works with adolescents and adults in individual therapy, as well as couples and groups. Many of the people she works with come to therapy feeling overwhelmed, emotionally drained, or unsure how to move forward. She focuses on creating a calm and steady space where clients can talk through what they are experiencing and begin working through those challenges in a way that feels manageable and supportive. As an affirming therapist, she works well with clients who are navigating questions around identity, relationships, and personal values. She aims to provide a space where people feel respected, understood, and supported as they explore the parts of themselves that may feel uncertain or difficult to express elsewhere.\n\nHer approach to therapy is collaborative and flexible. She believes that each person's experiences, relationships, and cultural background shape the way they move through the world, and therapy should reflect that. Drawing from approaches such as cognitive behavioral therapy, dialectical behavior therapy, acceptance and commitment therapy, and psychodynamic perspectives, she supports clients in building healthier coping strategies, strengthening relationships, and developing greater emotional balance.\n\nShe earned her Bachelor of Science in Public Health from the University of Massachusetts Amherst, with minors in Psychology and Spanish, and completed her Master of Social Work at Fordham University. As a bilingual therapist, she provides therapy in both English and Spanish and enjoys working with clients from diverse cultural backgrounds.",
    modalities: ["CBT", "DBT", "ACT", "Psychodynamic"],
    availability: "Accepting clients",
    accentColor: SENIOR,
    accentBg: SENIOR_BG,
    tagColor: SENIOR,
    tagBg: SENIOR_BG,
    specialties: [
      "Anxiety",
      "Identity",
      "Couples Therapy",
      "Group Therapy",
      "Relationship Challenges",
      "Cultural Identity",
      "Emotional Regulation",
    ],
    education: [
      "MSW, Fordham University",
      "BS in Public Health, University of Massachusetts Amherst",
    ],
    approach:
      "Jacqueline's approach is collaborative and flexible, drawing from CBT, DBT, ACT, and psychodynamic perspectives. She believes therapy should reflect each person's unique experiences, relationships, and cultural background.",
    languages: ["English", "Spanish"],
    scheduleNote: "Telehealth: New York",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Teens", "Adults", "Couples", "Groups"],
    ...providerAvailabilityFor("jacqueline-suarez"),
    isAssociate: false,
  },

  // ── Jeika Matthew - Founder + Senior Psychotherapist ──────────────────────
  {
    id: "jeika-matthew",
    name: "Jeika Matthew",
    credentials: "MA, LMHC-D, LPC, NCC",
    role: "Founder + Senior Psychotherapist",
    type: "clinical",
    price: "$200 / session",
    individualPrice: "$200",
    couplesPrice: "$200",
    priceNote: "",
    focus: "Anxiety · Trauma · Couples",
    shortBio:
      "Jeika is the founder of Perspective Therapy and a licensed psychotherapist with a multicultural, trauma-informed approach. She works with individuals and couples navigating anxiety, trauma, relationship challenges, and complex emotional dynamics.",
    fullBio:
      "Jeika Matthew is a psychotherapist, but she is also a creative, a nature lover, a bibliophile, a friend, a sibling, and a third-culture individual (Kittitian-Nevisian-American). She shares this because people are complex. Our identities rarely exist in isolation, and the ways they intersect shape how we experience relationships, stress, purpose, and growth. Jeika approaches therapy with the understanding that every person brings a unique combination of experiences, identities, and perspectives. She believes meaningful work begins when those complexities are acknowledged rather than simplified. Her goal is to create a space where those parts of you can be explored with curiosity, honesty, and respect.\n\nOne of the strengths Jeika brings to therapy is her ability to recognize patterns within individuals, relationships, and the dynamics that keep people feeling stuck. Clients often describe this as finally having someone connect the dots. She tends to notice underlying themes and connections that are often overlooked and helps clients make sense of them in ways that lead to meaningful change. She also works particularly well with neurodivergent clients and has a strong appreciation for the complexity, creativity, and challenges that can come with neurodivergent thinking.\n\nJeika works with adolescents and adults in individual therapy, as well as couples, many of whom are navigating relationship challenges, communication difficulties, and complex emotional dynamics. In her work with couples, she helps partners better understand the patterns shaping their relationship so they can move from cycles of frustration toward deeper understanding and connection. Throughout her career, she has supported individuals working through anxiety, depression, trauma, personality patterns, codependency, eating disorders, and relational challenges. Her approach is eclectic and grounded in a multicultural, trauma-informed framework that adapts to the unique needs of each client. She holds a Master of Arts in Mental Health and Wellness Counseling from New York University.",
    modalities: [
      "Trauma-Informed",
      "Psychodynamic",
      "Multicultural",
      "ACT",
      "REBT",
      "Sex Therapy",
      "AEDP",
      "Brainspotting",
      "Somatic Therapy",
      "Existential Therapy",
      "Neurodivergent-affirming",
      "Gottman Method",
      "Reality Therapy",
    ],
    availability: "Accepting clients",
    accentColor: SENIOR,
    accentBg: SENIOR_BG,
    tagColor: SENIOR,
    tagBg: SENIOR_BG,
    specialties: [
      "Anxiety",
      "Trauma",
      "Couples Therapy",
      "Neurodivergent Clients",
      "Identity",
      "Codependency",
    ],
    education: ["MA in Mental Health and Wellness Counseling, New York University"],
    approach:
      "Jeika's approach is eclectic and grounded in a multicultural, trauma-informed framework. She adapts her methods to the unique needs of each client, drawing from evidence-based and insight-oriented approaches that support deeper insight, emotional growth, and meaningful, lasting change.",
    languages: ["English"],
    scheduleNote:
      "In-person: Larchmont, NY · Telehealth: New York, New Jersey, and Indiana",
    ctaText: "Book Now",
    ctaLink: ACTIVE_BOOKING_URL,
    worksWith: ["Teens", "Adults", "Couples"],
    ...providerAvailabilityFor("jeika-matthew"),
    isAssociate: false,
  },

  // ── Lawrence Tiu - Administrative Assistant ────────────────────────────────
  {
    id: "lawrence-tiu",
    name: "Lawrence Tiu",
    credentials: "RN, RM",
    role: "Administrative Assistant",
    type: "support",
    price: "",
    individualPrice: "",
    priceNote: "",
    focus: "",
    shortBio:
      "Lawrence oversees the practice's daily operations, including intake, scheduling, billing, and client communications. He is often the first friendly voice clients hear when reaching out to Perspective Therapy.",
    fullBio:
      "Lawrence Tiu brings over a decade of experience in the healthcare BPO industry to Perspective Therapy. In his role, he oversees the practice's daily operations, including intake, appointment scheduling, billing, email, and social media management. He plays a key part in maintaining the practice's overall presence, ensuring that clients and community members experience the warmth, inclusivity, and authenticity of Perspective Therapy in both in-person and virtual settings.\n\nAs a licensed nurse and midwife with a strong background in customer service, Lawrence is dedicated to fostering trust and supporting seamless care coordination. He approaches his work with empathy and a genuine commitment to meaningful human connection.\n\nOutside of work, Lawrence enjoys traveling and exploring new cultures, having visited nine countries across Asia. These experiences have deepened his appreciation for diversity and broadened his perspective. He also stays active through badminton, pickleball, hiking, and freediving, and values spending quality time with his family on weekends.",
    modalities: [],
    availability: "",
    accentColor: SUPPORT,
    accentBg: SUPPORT_BG,
    tagColor: SUPPORT,
    tagBg: SUPPORT_BG,
    specialties: [],
    education: ["Registered Nurse (RN)", "Registered Midwife (RM)"],
    approach:
      "Lawrence brings a patient-centered mindset to every interaction. He understands that reaching out for therapy can feel overwhelming, so he works to make the intake process as smooth and reassuring as possible.",
    languages: ["English"],
    scheduleNote: "",
    ctaText: "Contact the Practice",
    ctaLink: "/contact",
    isAssociate: false,
  },
];

export const clinicalTeam = teamMembers.filter((m) => m.type === "clinical");
export const supportTeam = teamMembers.filter((m) => m.type === "support");

// Legacy export for home page component
export interface Therapist {
  id: string;
  name: string;
  credentials: string;
  role: string;
  price: string;
  individualPrice: string;
  couplesPrice?: string;
  priceNote: string;
  focus: string;
  shortBio: string;
  fullBio: string;
  modalities: string[];
  img?: string;
  availability: string;
  accentColor: string;
  accentBg: string;
  tagColor: string;
  tagBg: string;
  specialties: string[];
  education: string[];
  approach: string;
  languages?: string[];
  scheduleNote?: string;
  worksWith?: string[];
  acceptingNewClients?: boolean;
  acceptingIndividualClients?: boolean;
  acceptingCouplesClients?: boolean;
  acceptingChildClients?: boolean;
  acceptingTeenClients?: boolean;
  acceptingAdultClients?: boolean;
  acceptingGroupClients?: boolean;
  serviceFormats?: ("in-person" | "telehealth")[];
  currentAvailabilityFormats?: ("in-person" | "telehealth")[];
  isSpanishSpeaking?: boolean;
  availabilityNote?: string;
}

export const therapists: Therapist[] = clinicalTeam.slice(0, 3).map((m) => ({
  id: m.id,
  name: m.name,
  credentials: m.credentials,
  role: m.role,
  price: m.price,
  individualPrice: m.individualPrice,
  couplesPrice: m.couplesPrice,
  priceNote: m.priceNote,
  focus: m.focus,
  shortBio: m.shortBio,
  fullBio: m.fullBio,
  modalities: m.modalities,
  availability: m.availability,
  accentColor: m.accentColor,
  accentBg: m.accentBg,
  tagColor: m.tagColor,
  tagBg: m.tagBg,
  specialties: m.specialties,
  education: m.education,
  approach: m.approach,
  languages: m.languages,
  scheduleNote: m.scheduleNote,
  worksWith: m.worksWith,
  acceptingNewClients: m.acceptingNewClients,
  acceptingIndividualClients: m.acceptingIndividualClients,
  acceptingCouplesClients: m.acceptingCouplesClients,
  acceptingChildClients: m.acceptingChildClients,
  acceptingTeenClients: m.acceptingTeenClients,
  acceptingAdultClients: m.acceptingAdultClients,
  acceptingGroupClients: m.acceptingGroupClients,
  serviceFormats: m.serviceFormats,
  currentAvailabilityFormats: m.currentAvailabilityFormats,
  isSpanishSpeaking: m.isSpanishSpeaking,
  availabilityNote: m.availabilityNote,
}));

// Kinder uses her corrected public URL; all other profile routes stay unchanged.
export const therapistProfilePath = (id: string) =>
  id === "kinder-bradley" ? "/kinder-bradley" : `/therapists/${id}`;
